/**
 * photoPreview — iOS-safe photo-to-preview-URL + IndexedDB-safe encode utility.
 *
 * iOS Safari / Chrome-iOS evict the backing data of `URL.createObjectURL(file)`
 * under memory pressure, which is routinely triggered when the native camera
 * (`capture="environment"`) hands control back to Safari. The resulting blob
 * URL then renders as an empty `<img>` outline and `getBoundingClientRect()`
 * returns a zero-sized rect, so pin placement has nothing to land on.
 *
 * Convert the Blob to a self-contained base64 data URL on the same task that
 * received it. A data URL has no external backing store, so it cannot be
 * evicted — it survives background/foreground transitions, memory pressure,
 * and the `PhotoReview` overlay reopening from a thumbnail tile later.
 *
 * The same materialization is required before IndexedDB persistence
 * (`surveyStore.savePhoto`): WebKit/Firefox cannot reliably store Blob/File
 * objects, and File-backed blobs can become unreadable after the file picker
 * closes. Capture paths should call `encodeBlobForStorage` once and reuse the
 * data URL for both preview and storage.
 *
 * Rule of thumb: **any captured-photo preview URL kept in React state, in any
 * route, must go through this helper. Do not call URL.createObjectURL on a
 * File or a Blob reconstituted from iOS-disk-backed storage for preview
 * purposes.**
 */

const FILE_READER_TIMEOUT_MS = 4_000

const STORAGE_READ_FAILED =
  'Photo capture failed — device storage could not read the image. Please retake the photo.'

function isUsableDataUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('data:') && value.includes(',') && value.length > 'data:,'.length
}

function arrayBufferToDataUrl(buffer: ArrayBuffer, mime: string): string {
  const bytes = new Uint8Array(buffer)
  // Chunked binary build avoids call-stack / argument limits on large photos.
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...chunk)
  }
  return `data:${mime || 'application/octet-stream'};base64,${btoa(binary)}`
}

function fileReaderDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (isUsableDataUrl(result)) {
        resolve(result)
        return
      }
      reject(new Error('FileReader returned empty or non-data-URL result'))
    }
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

/**
 * Convert a Blob/File to a self-contained data URL for React preview state.
 * Prefer `encodeBlobForStorage` when the same bytes will also be persisted.
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return fileReaderDataUrl(blob)
}

/** Reconstruct an in-memory Blob from a data URL (not File-backed). */
export function dataUrlToBlob(dataUrl: string): Blob {
  if (!isUsableDataUrl(dataUrl)) {
    throw new Error('Invalid data URL')
  }
  const [header, b64] = dataUrl.split(',')
  const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream'
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/**
 * Multi-strategy encode for IndexedDB-safe photo persistence (HCP#1664).
 *
 * Order:
 *  1. `arrayBuffer` + manual base64 — deterministic; avoids FileReader hangs
 *     documented on Firefox/Playwright (see surveyStore history).
 *  2. `FileReader.readAsDataURL` with a hard timeout.
 *  3. `fetch(objectURL)` → arrayBuffer as a last-ditch path.
 *
 * Always clones via `blob.slice` first so transfer/detach of the original
 * File (or concurrent readers) cannot empty the bytes mid-encode.
 */
export async function encodeBlobForStorage(blob: Blob, mimeHint?: string): Promise<string> {
  if (!blob || typeof blob.size !== 'number') {
    throw new Error(STORAGE_READ_FAILED)
  }

  const mime = (blob.type || mimeHint || 'application/octet-stream').trim() || 'application/octet-stream'
  // Detach from the original File/Blob handle before any async read.
  const source =
    typeof blob.slice === 'function'
      ? blob.slice(0, blob.size, mime)
      : blob

  // Strategy 1 — arrayBuffer (preferred)
  try {
    const buffer = await source.arrayBuffer()
    if (buffer.byteLength === 0 && source.size > 0) {
      throw new Error('arrayBuffer returned empty for non-empty blob')
    }
    const dataUrl = arrayBufferToDataUrl(buffer, mime)
    if (isUsableDataUrl(dataUrl)) return dataUrl
  } catch {
    // continue
  }

  // Strategy 2 — FileReader with timeout (widest legacy compat)
  try {
    const dataUrl = await withTimeout(fileReaderDataUrl(source), FILE_READER_TIMEOUT_MS, 'FileReader')
    if (isUsableDataUrl(dataUrl)) return dataUrl
  } catch {
    // continue
  }

  // Strategy 3 — fetch via object URL
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' && typeof fetch === 'function') {
    let objectUrl: string | null = null
    try {
      objectUrl = URL.createObjectURL(source)
      const response = await fetch(objectUrl)
      if (!response.ok) throw new Error(`objectURL fetch failed: ${response.status}`)
      const buffer = await response.arrayBuffer()
      const dataUrl = arrayBufferToDataUrl(buffer, mime)
      if (isUsableDataUrl(dataUrl)) return dataUrl
    } catch {
      // continue to fail closed
    } finally {
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl)
        } catch {
          /* ignore */
        }
      }
    }
  }

  console.error('[photoPreview] Could not read photo blob for local storage')
  throw new Error(STORAGE_READ_FAILED)
}

export const PHOTO_STORAGE_READ_FAILED_MESSAGE = STORAGE_READ_FAILED
